<template>
  <div :class="$style.container">
    <div>
      <label :class="$style.label">
        Views
      </label>
      <IconTooltip>
        <p>
          The Views list includes shortcuts to predefined and saved views.
          Predefined views are helpful for common administrator tasks.
          Saved views are configured by you for your organization.
          To save a view - add filters, columns, and a time range, then click
          <strong>save view</strong>.
          Learn more in our
          <a
            :href="kbArticleLink"
            rel="noopener noreferrer"
            target="_blank"
          >Knowledge Base</a>.
        </p>
      </IconTooltip>
    </div>
    <DropDownContainer
      v-model="dropDownVisible"
    >
      <template v-slot:target>
        <button
          :class="$style.button"
          @click="handleButtonClick"
        >
          {{ value ? value.name : 'select view...' }}
          <span
            v-show="currentValueEdited"
            :class="$style.editedText"
          >
            - edited
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" :class="$style.caret" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M16 5c0 .27-.099.505-.297.703l-7 7A.961.961 0 018 13a.961.961 0 01-.703-.297l-7-7A.961.961 0 010 5c0-.27.099-.505.297-.703A.961.961 0 011 4h14c.27 0 .505.099.703.297A.961.961 0 0116 5z"/></svg>
        </button>
      </template>
      <template v-slot:dropDown>
        <div :class="[$style.dropDownContainer, $style.dropDown]">
          <div
            v-for="section in sections"
            :key="section.title"
          >
            <div
              :class="$style.sectionTitle"
              @click="() => section.visible = !section.visible"
            >
              <h5>{{ section.title }}</h5>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" :class="[$style.sectionCaret, {
                  [$style.sectionCaretOpen]: section.visible,
                }]" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M327.53 955.503l-75.309-74.658a24.503 24.503 0 01-7.275-17.454 24.497 24.497 0 017.129-17.308L562.04 533.576c4.404-4.44 7.126-10.555 7.126-17.306s-2.722-12.866-7.128-17.307l-318.45-321.051a24.498 24.498 0 01-7.126-17.306c0-6.823 2.78-12.996 7.27-17.449l75.311-74.655a24.497 24.497 0 0117.301-7.122 24.5 24.5 0 0117.447 7.268l426.768 430.315c4.404 4.44 7.126 10.555 7.126 17.306s-2.722 12.866-7.128 17.307L362.289 955.354a24.5 24.5 0 01-17.455 7.276 24.496 24.496 0 01-17.307-7.128z"/></svg>
            </div>
            <ul
              v-show="section.visible"
              :class="$style.optionsList"
            >
              <li
                v-for="option in section.options"
                :key="option.name"
                @click="handleViewChange(option)"
              >
                <span :class="$style.optionName">
                  {{ option.name }}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" v-if="section.allowDelete" :class="$style.deleteIcon" v-bind:svg-inline="''" @click.stop="handleDeleteClick(option)" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#54565a" d="M6.303 12.937l1.616 14.24c.087.677 3.353 2.9 8.081 2.903 4.728-.003 7.995-2.226 8.082-2.903l1.616-14.24c-2.469 1.381-6.161 2.036-9.697 2.036s-7.228-.655-9.697-2.036zm14.342-8.8l-1.26-1.397c-.486-.691-1.014-.82-2.041-.82h-2.686c-1.028 0-1.555.129-2.042.82l-1.26 1.397c-3.77.659-6.502 2.406-6.502 3.693v.25c0 2.268 4.991 4.107 11.147 4.107s11.147-1.838 11.147-4.107v-.25c0-1.287-2.732-3.034-6.502-3.693zm-1.61 3.267L17.199 5.44h-2.398l-1.836 1.964h-2.493s2.732-3.256 3.097-3.697a1.12 1.12 0 01.909-.467h.025-.001 3.02c.373 0 .704.183.908.464l.002.003c.364.442 3.097 3.697 3.097 3.697z"/></svg>
              </li>
            </ul>
            <div
              v-show="section.visible && !section.options.length"
              :class="$style.noOptionsText"
            >
              No views created yet
            </div>
          </div>
        </div>
      </template>
    </DropDownContainer>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import { quickViewMetadata, quickViews } from 'client/Insights/ActivityLog/constants/ActivityLogViewConsts';
import DropDownContainer from 'components/DropDownMenu/DropDownContainer.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import IconTooltip from 'components/Tooltip/IconTooltip.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';

const { ItemTypeConsts, StoreModuleMap } = Constants;
const { error, success } = Constants.default;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const kbArticleLink = KbArticleUrls.activityLogViewData;

const quickViewsToDisplay = [
  quickViews.loginAttempts,
  quickViews.passwordChanges,
  quickViews.userStateChanges,
  quickViews.associationChanges,
  quickViews.userCreatesAndDeletes,
  quickViews.systemCreatesAndDeletes,
  quickViews.adminCreatesAndDeletes,
  quickViews.ssoAuths,
  quickViews.adminActivity,
];

export default {
  name: 'ActivityLogViewsDropdown',
  components: {
    DropDownContainer,
    IconTooltip,
  },

  props: {
    currentValueEdited: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => ({}),
      validator: val => !!val.name,
    },
  },

  data: () => ({
    dropDownVisible: false,
    kbArticleLink,
    quickViewSection: {
      title: 'Quick Views',
      visible: true,
    },
    savedViewSection: {
      allowDelete: true,
      title: 'Saved Views',
      visible: true,
    },
  }),

  computed: {
    ...mapState(insightsEventModule, [
      'filterOptions',
      'savedViews',
    ]),

    sections() {
      const sections = [
        this.savedViewSection,
        this.quickViewSection,
      ];
      sections[0].options = [...this.savedViews].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      sections[1].options = quickViewsToDisplay.map(view => quickViewMetadata[view]);
      return sections;
    },
  },

  created() {
    // Fetch saved views so they are already loaded when client opens the Views dropdown
    this.fetchSavedViews();
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'deleteSavedView',
      'fetchSavedViews',
    ]),

    deleteView(view) {
      return this.deleteSavedView(view)
        .then(() => {
          notify(success, 'Saved View successfully deleted.');
        })
        .catch(() => {
          notify(error, 'Error deleting Saved View.');
        });
    },

    handleButtonClick() {
      this.dropDownVisible = !this.dropDownVisible;
    },

    handleDeleteClick(view) {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          mdTemplate: `Are you sure you want to delete the Saved View, **${view.name}**?`,
          continueText: 'delete',
          handleContinue: () => {
            this.deleteView(view)
              .finally(ModalEventBus.close);
          },
          headerText: 'Delete Saved View',
          mainActionAttention: true,
        },
      });
    },

    handleViewChange(view) {
      this.$emit('input', view);
      this.dropDownVisible = false;

      EventHelper.triggerActivityLogFilterTemplateSelected({
        template: view.name,
      });
    },
  },
};
</script>

<style module>
@value (gradientDark4, gradientLight2, gradientLight4, greyDark1) from '~css/colors.css';
@value (cardShadow1) from '~css/shadows.css';

.container { width: 280px; }

.label {
  composes: label from "~components/FormValidation/css/formFields.css";
  composes: label-color from "~components/FormValidation/css/formFields.css";
  composes: label-font from "~components/FormValidation/css/formFields.css";
  composes: label-position from "~components/FormValidation/css/formFields.css";
  composes: label-typography from "~components/FormValidation/css/formFields.css";
}

.button {
  background-color: gradientLight2;
  border: 2px solid gradientLight4;
  color: greyDark1 !important; /* overriding .jc-ux2 button legacy styles */
  height: 30px;
  overflow: hidden !important; /* overriding .jc-ux2 button legacy styles */
  padding: 5px 25px 5px 10px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.editedText {
  color: gradientDark4;
  margin-left: 1px;
}

.caret {
  height: 9px;
  opacity: 0.35;
  position: absolute;
  right: 12px;
  top: 10px;
}

.caret:focus { outline: none; }

.dropDown {
  background-color: white;
  border: 1px solid gradientLight4;
  border-radius: 4px;
  box-shadow: cardShadow1;
  min-width: 280px;
  overflow: auto;
  padding: 4px 0;
}

.sectionTitle {
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 10px 15px;
}

.sectionTitle:hover { background-color: #f5f5f5; }

.sectionTitle > h5 {
  font-size: 13px !important;
  font-weight: bold !important;
  margin: 0;
}

.sectionCaret {
  height: 12px;
  opacity: 0.6;
  transform: rotate(90deg);
  transition: all 0.2s ease-in-out;
  width: 12px;
}

.sectionCaret:focus { outline: none; }
.sectionCaretOpen { transform: rotate(270deg); }

.optionsList {
  margin: 0 !important;
  padding: 0;
}

.optionsList li {
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  list-style-type: none;
  padding: 3px 15px 3px 25px;
  user-select: none;
}

.optionsList li:hover {
  background-color: #f5f5f5; /* b-dropdown-item hover color */
}

.optionName { flex: 1; }

.deleteIcon {
  height: 13px;
  opacity: 0.8;
  visibility: hidden;
  width: 13px;
}

.deleteIcon:focus { outline: none; }
.deleteIcon:hover { opacity: 1; }

.optionsList li:hover .deleteIcon {
  visibility: visible;
}

.noOptionsText {
  font-size: 12px;
  font-style: italic;
  margin: 5px 0;
  text-align: center;
}
</style>
